import {type FC, useContext, useMemo} from 'react';

import Color from 'color';

import type {Props} from './types';

import {ThemeContext} from '../../../../styles/ThemeProvider';
import {AvatarPlaceholder} from '../../../../uikit/AvatarPlaceholder';
import {Clickable} from '../../../../uikit/Clickable';
import {Defs, LinearGradient, Stop} from '../../../../uikit/Svg';
import {View} from '../../../../uikit/View';
import {getFileSrc} from '../../../../utils/entity/file';
import * as S from './styles';

export const NotificationsListItemDumb: FC<Props> = ({
  avatar,
  avatarPlaceholder,
  buttons,
  date,
  icon,
  iconGradient,
  message,
  onButtonClick,
  onClick,
  renderMessage,
  specificTestID,
  subAvatar,
  subAvatarPlaceholder,
  testID,
  title,
}) => {
  const {theme} = useContext(ThemeContext);
  const Icon = useMemo(() => icon, [icon]);

  const parsedTitle = useMemo(() => {
    return title
      .split(/<%(.*?)%>/)
      .filter(Boolean)
      .map((part, i) => {
        const key = `${part}-${i}`;

        if (part.includes('highlight->')) {
          return (
            <S.HighlightedTitle key={key}>
              {part.replace('highlight->', '')}
            </S.HighlightedTitle>
          );
        }

        if (part.includes('danger->')) {
          return (
            <S.DangerTitle key={key}>
              {part.replace('danger->', '')}
            </S.DangerTitle>
          );
        }

        if (part.includes('sub->')) {
          return <S.SubTitle key={key}>{part.replace('sub->', '')}</S.SubTitle>;
        }

        return part;
      });
  }, [title]);

  const iconGradientColors = useMemo(() => {
    let colors: string[] = [theme.color.v2.global1, theme.color.v2.global2];

    switch (iconGradient) {
      case 'danger':
        colors = [
          theme.color.v2.error,
          Color(theme.color.v2.error).darken(0.1).toString(),
        ];
        break;
      case 'info':
        colors = [
          theme.color.v2.global2,
          Color(theme.color.v2.global2).darken(0.1).toString(),
        ];
        break;
      case 'success':
        colors = [
          theme.color.v2.global1,
          Color(theme.color.v2.global1).darken(0.1).toString(),
        ];
        break;
      default:
        break;
    }

    return colors;
  }, [iconGradient, theme]);

  const messageToRender = message ? (
    <S.Message>{message}</S.Message>
  ) : (
    (renderMessage?.() ?? null)
  );

  return (
    <Clickable onClick={onClick}>
      <S.Container testID={testID}>
        <S.AvatarContainer>
          <S.Avatar
            placeholder={
              avatarPlaceholder ? (
                <AvatarPlaceholder text={avatarPlaceholder} />
              ) : undefined
            }
            src={getFileSrc(avatar, 250).url}
          />
          {Icon ? (
            <S.IconContainer>
              <S.IconGradientSvg>
                <Defs>
                  <LinearGradient id={iconGradient} x1={0} x2={1} y1={0} y2={1}>
                    {iconGradientColors.map((color, i, itself) => (
                      <Stop
                        key={color}
                        offset={
                          i === itself.length - 1
                            ? 1
                            : i * (1 / iconGradientColors.length)
                        }
                        stopColor={color}
                        stopOpacity="1"
                      />
                    ))}
                  </LinearGradient>
                </Defs>
                <S.IconGradientRect
                  fill={`url(#${iconGradient})`}
                  x="0"
                  y="0"
                />
              </S.IconGradientSvg>

              <Icon fill={theme.color.v3.icon.white} style={S.icon} />
            </S.IconContainer>
          ) : null}
          {subAvatar || subAvatarPlaceholder ? (
            <S.SubAvatar
              placeholder={
                subAvatarPlaceholder ? (
                  <AvatarPlaceholder text={subAvatarPlaceholder} />
                ) : undefined
              }
              src={getFileSrc(subAvatar, 250).url}
            />
          ) : null}
        </S.AvatarContainer>
        <S.ContentContainer>
          <S.Title>{parsedTitle}</S.Title>
          {messageToRender ? (
            <S.MessageContainer>{messageToRender}</S.MessageContainer>
          ) : null}
          {buttons?.length ? (
            <S.ButtonsContainer>
              {buttons.map(({disabled, id, label, scheme = 'primary'}) => (
                <S.NotificationButton
                  disabled={disabled}
                  key={id}
                  onClick={e => {
                    e.event.DOMEvent?.stopPropagation();
                    onButtonClick?.(id);
                  }}
                  scheme={scheme}
                >
                  {label}
                </S.NotificationButton>
              ))}
            </S.ButtonsContainer>
          ) : null}
          <S.Date>{date}</S.Date>
        </S.ContentContainer>
        <View testID={specificTestID} />
      </S.Container>
    </Clickable>
  );
};
